import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface MetricOption {
  key: string;
  label: string;
  color: string;
}

interface TrendChartProps {
  data: any[];
  metrics: MetricOption[];
  defaultMetric?: string;
  title?: string;
  showAllByDefault?: boolean;
}

/**
 * TrendChart Component
 * Displays a time-series chart with toggleable metrics
 */
const TrendChart: React.FC<TrendChartProps> = ({
  data,
  metrics,
  defaultMetric,
  title = "Trend Over Time",
  showAllByDefault = false
}) => {
  const [selectedMetric, setSelectedMetric] = useState(
    showAllByDefault ? 'all' : (defaultMetric || metrics[0]?.key || '')
  );

  const selectedMetricInfo = metrics.find(m => m.key === selectedMetric);

  return (
    <div className="tekkr-chart-section">
      {/* Chart Header with Toggles */}
      <div className="tekkr-chart-header">
        <h3 className="tekkr-chart-title">{title}</h3>
        <div className="tekkr-chart-toggles">
          <button
            className={`tekkr-chart-toggle-btn ${selectedMetric === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedMetric('all')}
          >
            All Metrics
          </button>
          {metrics.map((metric) => (
            <button
              key={metric.key}
              className={`tekkr-chart-toggle-btn ${selectedMetric === metric.key ? 'active' : ''}`}
              onClick={() => setSelectedMetric(metric.key)}
            >
              {metric.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Container */}
      <div className="tekkr-chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#2d3139" />
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: '0.75rem' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e2028',
                border: '1px solid #2d3139',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                color: '#e5e7eb'
              }}
            />
            {selectedMetric === 'all' ? (
              // Show all metrics
              metrics.map((metric) => (
                <Line
                  key={metric.key}
                  type="monotone"
                  dataKey={metric.key}
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={false}
                  name={metric.label}
                />
              ))
            ) : (
              // Show single selected metric
              <Line
                type="monotone"
                dataKey={selectedMetric}
                stroke={selectedMetricInfo?.color || '#e91e8c'}
                strokeWidth={3}
                dot={{ fill: selectedMetricInfo?.color || '#e91e8c', r: 4 }}
                activeDot={{ r: 6 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;

